(function (global) {
  const cartStates = new WeakMap();
  const started = new WeakSet();
  const watchedCarts = new WeakSet();

  function trimRating(n) {
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  function ratingLabel(filled, total, nearby) {
    const text = String(nearby || '').replace(/\s+/g, ' ');
    const match = text.trim().match(/^(\d+(?:\.\d+)?)\s*(?:\/\s*5|out of 5(?: stars)?|stars)$/i);
    if (match) {
      const n = parseFloat(match[1]);
      if (!Number.isNaN(n) && n >= 0 && n <= 5) {
        return trimRating(n) + ' out of 5 stars';
      }
    }
    return Math.min(filled, 5) + ' of ' + Math.min(total, 5) + ' stars';
  }

  function shouldSetListRole(tag, role) {
    const name = String(tag || '').toUpperCase();
    const implicitList = (name === 'UL' || name === 'OL') && !role;
    return !implicitList && role !== 'list';
  }

  function compactText(el) {
    return ((el && el.textContent) || '').replace(/\s+/g, ' ').trim();
  }

  function accessibleName(el) {
    if (!el) return '';
    const labelled = el.getAttribute('aria-label') || el.getAttribute('title');
    if (labelled && labelled.trim()) return labelled.trim();
    const text = compactText(el);
    if (text && !/^[×x✕+＋\-−–]$/i.test(text)) return text;
    const img = el.querySelector && el.querySelector('img[alt]');
    if (img && img.getAttribute('alt')) return img.getAttribute('alt').trim();
    return '';
  }

  function labelRebuyButton(className, text) {
    const c = String(className || '');
    const t = String(text || '').trim();
    if (t && !/^[×x✕+＋\-−–]$/i.test(t) && t.length > 1) return null;
    if (/flyout-item-remove|item-remove|cart__remove/i.test(c)) return 'Remove item';
    if (/flyout-close|cart-close|cart__close/i.test(c)) return 'Close cart';
    if (/quantity-widget-button|quantity-widget__down|quantity-minus/i.test(c) && /decrease|minus|^[-−–]$/i.test(t)) {
      return 'Decrease quantity';
    }
    if (/quantity-widget-button|quantity-widget__up|quantity-plus/i.test(c) && /increase|plus|^[+＋]$/i.test(t)) {
      return 'Increase quantity';
    }
    if (/^[-−–]$/.test(t)) return 'Decrease quantity';
    if (/^[+＋]$/.test(t)) return 'Increase quantity';
    if (/^[×x✕]$/i.test(t) && /close/i.test(c)) return 'Close cart';
    return null;
  }

  function hideSprite(doc) {
    const sprite = doc.getElementById('junipStarSymbols');
    if (!sprite) return;
    sprite.setAttribute('aria-hidden', 'true');
    sprite.setAttribute('focusable', 'false');
  }

  function patchJunipRatings(doc) {
    doc.querySelectorAll('.junip-star-ratings-container, .junip-product-summary-star-ratings-container').forEach(function (container) {
      const summary = container.closest('.junip-product-summary');
      const average = container.parentElement.querySelector('.junip-product-review-average');
      const filled = container.querySelector('.junip-stars-filled');
      let value = summary && summary.getAttribute('data-product-rating-average');
      if (!value && average) value = compactText(average);
      if (!value && filled && /^\d+(?:\.\d+)?%$/.test(filled.style.width)) {
        value = String(parseFloat(filled.style.width) / 20);
      }
      const rating = value && /^\d+(?:\.\d+)?$/.test(value) ? Number(value) : NaN;
      if (!Number.isFinite(rating) || rating < 0 || rating > 5) {
        if (container.getAttribute('data-jf-a11y') === '1') {
          container.removeAttribute('aria-label');
          container.removeAttribute('data-jf-a11y');
          if (container.getAttribute('role') === 'img') container.removeAttribute('role');
        }
        return;
      }
      container.querySelectorAll('svg').forEach(function (svg) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.removeAttribute('role');
      });

      let label = Number(rating.toFixed(2)) + ' out of 5 stars';
      if (summary) {
        const count = summary.getAttribute('data-product-rating-count');
        if (count && /^\d+$/.test(count)) label += ', ' + count + (count === '1' ? ' review' : ' reviews');
      }
      if (container.getAttribute('role') !== 'button') container.setAttribute('role', 'img');
      container.setAttribute('aria-label', label);
      container.setAttribute('data-jf-a11y', '1');
    });
  }

  function patchJunipSvgs(doc) {
    doc.querySelectorAll('[class*="junip"] svg[role="img"]').forEach(function (svg) {
      if (svg.closest('.junip-star-ratings-container')) return;
      if (svg.getAttribute('aria-hidden') === 'true') return;
      if (svg.getAttribute('aria-label')) return;
      const title = svg.querySelector('title');
      if (title && title.textContent.trim()) {
        svg.setAttribute('aria-label', title.textContent.trim());
      }
    });
  }

  function itemName(item) {
    const title = item.querySelector(
      '.rebuy-cart__flyout-item-product-title, .rebuy-product-title, .rebuy-cart__flyout-item-title'
    );
    return compactText(title) || compactText(item).slice(0, 80) || 'Cart item';
  }

  function syncCartInert(cart) {
    if (cart.getAttribute('aria-hidden') === 'true') {
      cart.setAttribute('inert', '');
      cart.setAttribute('data-jf-inert', '');
    } else if (cart.hasAttribute('data-jf-inert')) {
      cart.removeAttribute('inert');
      cart.removeAttribute('data-jf-inert');
    }
  }

  function patchHiddenCart(doc) {
    doc.querySelectorAll('.rebuy-cart').forEach(function (cart) {
      syncCartInert(cart);
      if (watchedCarts.has(cart) || typeof MutationObserver === 'undefined') return;
      watchedCarts.add(cart);
      new MutationObserver(function () {
        syncCartInert(cart);
      }).observe(cart, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
  }

  function patchRebuy(doc) {
    doc.querySelectorAll('.rebuy-cart__flyout-item').forEach(function (item) {
      const parent = item.parentElement;
      if (parent && shouldSetListRole(parent.tagName, parent.getAttribute('role'))) {
        parent.setAttribute('role', 'list');
      }
      if ((item.getAttribute('aria-label') || '').toLowerCase() === 'product') {
        item.setAttribute('aria-label', itemName(item));
      }
    });

    const flyout = doc.querySelector('.rebuy-cart__flyout') || doc.querySelector('.rebuy-cart');
    const open = !!(doc.body && /rebuy-cart-visible/.test(doc.body.className));
    if (flyout && open) {
      flyout.setAttribute('role', 'dialog');
      flyout.setAttribute('aria-modal', 'true');
      if (!flyout.getAttribute('aria-label') && !flyout.getAttribute('aria-labelledby')) {
        flyout.setAttribute('aria-label', 'Your cart');
      }
    }

    doc.querySelectorAll('.rebuy-cart button, .rebuy-cart__flyout button').forEach(function (btn) {
      if (accessibleName(btn)) return;
      let label = labelRebuyButton(btn.className, compactText(btn));
      if (!label && /quantity-widget-button/i.test(btn.className || '')) {
        const prev = btn.previousElementSibling;
        const prevIsQty = prev && /quantity-widget-button/i.test(prev.className || '');
        label = prevIsQty ? 'Increase quantity' : 'Decrease quantity';
      }
      if (label) btn.setAttribute('aria-label', label);
    });

    doc.querySelectorAll('a.rebuy-product-image').forEach(function (link) {
      if (accessibleName(link)) return;
      const host = link.closest('.rebuy-cart__flyout-item, .rebuy-product, [class*="rebuy"]');
      const title = host && host.querySelector(
        '.rebuy-product-title, .rebuy-product-title-link, .rebuy-cart__flyout-item-product-title'
      );
      const name = compactText(title);
      if (name) link.setAttribute('aria-label', name);
    });

    doc.querySelectorAll('.rebuy-cart__progress-bar-meter-fill').forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });
    doc.querySelectorAll('.rebuy-bundle-builder__group-container-body-items').forEach(function (el) {
      el.setAttribute('tabindex', '0');
    });
  }

  function ensureLiveRegion(doc) {
    if (doc.getElementById('jf-a11y-status')) return;
    const cart = doc.querySelector('.rebuy-cart__flyout');
    if (!cart) return;
    const el = doc.createElement('div');
    el.id = 'jf-a11y-status';
    el.className = 'visually-hidden';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    cart.appendChild(el);
  }

  function trackCartFocus(doc, state) {
    doc.addEventListener('focusin', function (event) {
      const source = event.target;
      const item = source.closest && source.closest('.rebuy-cart__flyout-item');
      if (!item) {
        state.focus = null;
        return;
      }
      state.focus = {
        source: source,
        name: itemName(item),
        index: Array.from(doc.querySelectorAll('.rebuy-cart__flyout-item')).indexOf(item),
        control: Array.from(item.querySelectorAll('a[href], button, input, select')).indexOf(source),
      };
    });
  }

  function restoreCartFocus(doc, state, items) {
    const previous = state.focus;
    if (!previous || previous.source.isConnected) return;
    state.focus = null;
    if (!doc.body.classList.contains('rebuy-cart-visible')) return;
    if (doc.activeElement !== doc.body && doc.activeElement !== doc.documentElement) return;
    const item = items[Math.min(previous.index, items.length - 1)];
    let target;
    if (item && itemName(item) === previous.name) {
      target = item.querySelectorAll('a[href], button, input, select')[previous.control];
    }
    if (!target && item) target = item.querySelector('.rebuy-cart__flyout-item-remove, a[href], button');
    if (!target) target = doc.querySelector('.rebuy-cart__flyout-close');
    if (target) target.focus();
  }

  function announceCart(doc) {
    const region = doc.getElementById('jf-a11y-status');
    if (!region) return;
    const items = Array.from(doc.querySelectorAll('.rebuy-cart__flyout-item'));
    const quantities = new Map();
    for (const item of items) {
      const label = item.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');
      if (!label) return;
      const text = Array.from(label.childNodes).filter(function (node) {
        return node.nodeType === 3;
      }).map(function (node) { return node.textContent; }).join('').trim();
      if (!/^\d+$/.test(text)) return;
      const name = itemName(item);
      quantities.set(name, (quantities.get(name) || 0) + Number(text));
    }
    let state = cartStates.get(doc);
    if (!state) {
      state = { quantities: quantities, focus: null };
      cartStates.set(doc, state);
      trackCartFocus(doc, state);
      return;
    }
    const messages = [];
    quantities.forEach(function (quantity, name) {
      const previous = state.quantities.get(name);
      if (previous === undefined) messages.push(name + ' added to cart. Quantity ' + quantity + '.');
      else if (previous !== quantity) messages.push(name + ' quantity updated to ' + quantity + '.');
    });
    state.quantities.forEach(function (quantity, name) {
      if (!quantities.has(name)) messages.push(name + ' removed from cart.');
    });
    if (messages.length) {
      const count = Array.from(quantities.values()).reduce(function (total, quantity) { return total + quantity; }, 0);
      messages.push(count ? 'Cart now has ' + count + (count === 1 ? ' item.' : ' items.') : 'Your cart is empty.');
      region.textContent = messages.join(' ');
    }
    state.quantities = quantities;
    restoreCartFocus(doc, state, items);
  }

  function patch(doc) {
    if (!doc || !doc.querySelectorAll) return;
    hideSprite(doc);
    patchJunipRatings(doc);
    patchJunipSvgs(doc);
    patchRebuy(doc);
    patchHiddenCart(doc);
    ensureLiveRegion(doc);
    announceCart(doc);
  }

  function start(doc) {
    if (started.has(doc)) return;
    started.add(doc);
    patch(doc);
    const root = doc.body || doc.documentElement;
    if (!root || typeof MutationObserver === 'undefined') return;
    let timer = 0;
    const observer = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        patch(doc);
      }, 100);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'value', 'data-product-rating-average', 'data-product-rating-count'],
    });
  }

  global.A11y = {
    ratingLabel,
    shouldSetListRole,
    labelRebuyButton,
    patch,
    start,
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        start(document);
      });
    } else {
      start(document);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
